// src/roles/store/EquipmentRegistry.jsx
//
// EQUIPMENT / MACHINE COST TRACKING — UI (D-187)
// Onboard existing stock, receive new equipment, issue to a project
// (creates a financial_transactions entry), return to store (creates a
// reversal entry). Equipment never touches Item Master.

import { useEffect, useState } from "react";
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../../firebase";
import {
  fetchAllEquipment,
  createManualEquipmentInstance,
  receiveEquipmentInstance,
  issueEquipmentToProject,
  returnEquipmentToStore,
  scrapEquipmentInstance,
  updateEquipmentLocation,
  fetchEquipmentHistory,
} from "../../services/equipmentService";

const HVAC_BRANDS = [
  "Daikin", "Voltas", "Blue Star", "Hitachi", "LG", "Carrier",
  "Mitsubishi Electric", "Toshiba", "Samsung", "Panasonic",
  "O General", "Godrej", "Lloyd", "IFB", "Bosch", "Trane", "York",
];

const STATUS_UI = {
  in_store:  { label: "In Store",  color: "bg-gray-100 text-gray-600" },
  installed: { label: "Installed", color: "bg-green-100 text-green-700" },
  scrapped:  { label: "Scrapped",  color: "bg-red-100 text-red-600" },
};

export default function EquipmentRegistry() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";
  const canManage  = ["owner", "sm", "manager", "store"].includes(user.role);

  const [equipment, setEquipment] = useState([]);
  const [projects,  setProjects]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [filter,    setFilter]    = useState("all"); // all | in_store | installed | scrapped

  const [showOnboardModal, setShowOnboardModal] = useState(false);
  const [onboardForm, setOnboardForm] = useState({
    item_name: "", item_code: "", brand: "", brandOther: "", capacity_tr: "",
    serial_no: "", purchase_rate: "", location: "Main Store",
  });
  const [onboardSaving, setOnboardSaving] = useState(false);

  const [issueTarget, setIssueTarget] = useState(null); // equipment being issued
  const [issueProjectId, setIssueProjectId] = useState("");
  const [issueSaving, setIssueSaving] = useState(false);

  const [returningId, setReturningId] = useState(null);
  const [scrappingId, setScrappingId] = useState(null);

  const [historyFor, setHistoryFor] = useState(null); // equipment_id
  const [historyLog, setHistoryLog] = useState([]);

  const [editLocationTarget, setEditLocationTarget] = useState(null);
  const [editLocationValue,  setEditLocationValue]  = useState("");
  const [savingLocation,     setSavingLocation]     = useState(false);

  const openEditLocation = (item) => {
    setEditLocationTarget(item);
    setEditLocationValue(item.location || "Main Store");
  };

  const handleSaveLocation = async () => {
    if (!editLocationValue.trim()) { alert("Location required"); return; }
    try {
      setSavingLocation(true);
      await updateEquipmentLocation({
        equipment_id: editLocationTarget.id, company_id, location: editLocationValue,
      }, user);
      setEditLocationTarget(null);
      await load();
    } catch (err) {
      alert(err.message || "Failed to update location");
    } finally {
      setSavingLocation(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const [eq, projSnap] = await Promise.all([
        fetchAllEquipment(company_id),
        getDocs(query(collection(db, "projects"), where("company_id", "==", company_id))),
      ]);
      setEquipment(eq);
      // Only show projects that actually have a site linked — same discipline
      // as fetchClientsWithSites(). Issuing equipment to a site-less project
      // silently loses the "which site is this equipment at" information,
      // and this exact gap already caused one production mix-up (equipment
      // shown as issued to a 0-site project when it was really at a
      // different site). See D-187 follow-up fix.
      const allProjects = projSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setProjects(allProjects.filter(p => p.site_id));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ── ONBOARD EXISTING STOCK ──────────────────────────────────────────────
  const handleOnboard = async () => {
    if (!onboardForm.item_name.trim() || !onboardForm.serial_no.trim()) {
      alert("Item name and Serial No. are required");
      return;
    }
    try {
      setOnboardSaving(true);
      const resolvedBrand = onboardForm.brand === "Other" ? onboardForm.brandOther.trim() : onboardForm.brand;
      await createManualEquipmentInstance({ ...onboardForm, brand: resolvedBrand }, user);
      setShowOnboardModal(false);
      setOnboardForm({ item_name: "", item_code: "", brand: "", brandOther: "", capacity_tr: "", serial_no: "", purchase_rate: "", location: "Main Store" });
      await load();
    } catch (err) {
      alert(err.message || "Failed to onboard");
    } finally {
      setOnboardSaving(false);
    }
  };

  // ── ISSUE TO PROJECT ─────────────────────────────────────────────────────
  const openIssueModal = (item) => {
    setIssueTarget(item);
    setIssueProjectId("");
  };

  const handleConfirmIssue = async () => {
    if (!issueProjectId) { alert("Please select a project"); return; }
    const project = projects.find(p => p.id === issueProjectId);
    try {
      setIssueSaving(true);
      await issueEquipmentToProject({
        equipment_id: issueTarget.id,
        company_id,
        project_id:   project.id,
        project_name: project.project_name || "",
        site_id:      project.site_id || "",
        site_name:    project.site_name || "",
      }, user);
      setIssueTarget(null);
      await load();
    } catch (err) {
      alert(err.message || "Issue failed");
    } finally {
      setIssueSaving(false);
    }
  };

  // ── RETURN TO STORE ──────────────────────────────────────────────────────
  const handleReturn = async (item) => {
    if (!window.confirm(`Return "${item.item_name}" (#${item.serial_no}) to store? This will reverse its cost from ${item.current_project_name}.`)) return;
    try {
      setReturningId(item.id);
      await returnEquipmentToStore({ equipment_id: item.id, company_id }, user);
      await load();
    } catch (err) {
      alert(err.message || "Return failed");
    } finally {
      setReturningId(null);
    }
  };

  // ── SCRAP ────────────────────────────────────────────────────────────────
  const handleScrap = async (item) => {
    if (!window.confirm(`Mark "${item.item_name}" (#${item.serial_no}) as scrapped? This cannot be undone.`)) return;
    try {
      setScrappingId(item.id);
      await scrapEquipmentInstance({ equipment_id: item.id, company_id }, user);
      await load();
    } catch (err) {
      alert(err.message || "Scrap failed");
    } finally {
      setScrappingId(null);
    }
  };

  // ── HISTORY ──────────────────────────────────────────────────────────────
  const openHistory = async (item) => {
    setHistoryFor(item);
    const log = await fetchEquipmentHistory(company_id, item.id);
    setHistoryLog(log);
  };

  const filtered = equipment.filter(e => filter === "all" || e.status === filter);

  if (loading) return <div className="p-6 text-gray-400">Loading...</div>;

  return (
    <div className="p-4 md:p-6 space-y-5 max-w-5xl mx-auto pb-20">

      {/* HEADER */}
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Equipment Registry</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Machines & serialized equipment — tracked separately from Item Master
          </p>
        </div>
        {canManage && (
          <button onClick={() => setShowOnboardModal(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700">
            + Add Existing Equipment
          </button>
        )}
      </div>

      {/* FILTER TABS */}
      <div className="flex gap-2 border-b border-gray-200">
        {["all", "in_store", "installed", "scrapped"].map(f => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              filter === f ? "border-green-600 text-green-700" : "border-transparent text-gray-500"
            }`}>
            {f === "all" ? `All (${equipment.length})` :
              `${STATUS_UI[f].label} (${equipment.filter(e => e.status === f).length})`}
          </button>
        ))}
      </div>

      {/* LIST */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <div className="text-5xl mb-3">🏭</div>
          <p className="font-semibold">No equipment found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(item => {
            const statusInfo = STATUS_UI[item.status] || STATUS_UI.in_store;
            return (
              <div key={item.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-gray-800">{item.item_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusInfo.color}`}>
                        {statusInfo.label}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      #{item.serial_no} · {item.brand || "—"} {item.capacity_tr ? `· ${item.capacity_tr} TR` : ""}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      ₹{Number(item.purchase_rate || 0).toLocaleString("en-IN")}
                      {item.status === "installed"
                        ? ` · at ${item.current_project_name}`
                        : item.location ? ` · ${item.location}` : ""}
                    </p>
                  </div>

                  {canManage && (
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => openHistory(item)}
                        className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200">
                        📜 History
                      </button>
                      {item.status === "in_store" && (
                        <>
                          <button onClick={() => openEditLocation(item)}
                            className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-xs font-semibold hover:bg-gray-200">
                            📍 Move
                          </button>
                          <button onClick={() => openIssueModal(item)}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700">
                            ↗ Issue to Project
                          </button>
                          <button onClick={() => handleScrap(item)}
                            disabled={scrappingId === item.id}
                            className="px-3 py-1.5 bg-red-100 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-200 disabled:opacity-40">
                            {scrappingId === item.id ? "..." : "🗑 Scrap"}
                          </button>
                        </>
                      )}
                      {item.status === "installed" && (
                        <button onClick={() => handleReturn(item)}
                          disabled={returningId === item.id}
                          className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-xs font-semibold hover:bg-purple-200 disabled:opacity-40">
                          {returningId === item.id ? "..." : "↩ Return to Store"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MODAL: ONBOARD EXISTING EQUIPMENT */}
      {showOnboardModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-lg font-bold text-gray-800">Add Existing Equipment</h2>
              <button onClick={() => setShowOnboardModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-gray-500">
                For equipment you already own — enter what it cost you originally.
              </p>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Item Name *</label>
                <input value={onboardForm.item_name}
                  onChange={e => setOnboardForm(f => ({ ...f, item_name: e.target.value }))}
                  placeholder="e.g. VRF Outdoor Unit 14HP"
                  className="border p-2 rounded-xl w-full text-sm outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Brand</label>
                  <select value={onboardForm.brand}
                    onChange={e => setOnboardForm(f => ({ ...f, brand: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
                    <option value="">— Select —</option>
                    {HVAC_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                    <option value="Other">Other</option>
                  </select>
                  {onboardForm.brand === "Other" && (
                    <input value={onboardForm.brandOther}
                      onChange={e => setOnboardForm(f => ({ ...f, brandOther: e.target.value }))}
                      placeholder="Enter brand name"
                      className="border p-2 rounded-xl w-full text-sm outline-none mt-2" />
                  )}
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Capacity (TR)</label>
                  <input value={onboardForm.capacity_tr}
                    onChange={e => setOnboardForm(f => ({ ...f, capacity_tr: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Serial No. *</label>
                  <input value={onboardForm.serial_no}
                    onChange={e => setOnboardForm(f => ({ ...f, serial_no: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 block mb-1">Purchase Rate ₹</label>
                  <input type="number" min="0" value={onboardForm.purchase_rate}
                    onChange={e => setOnboardForm(f => ({ ...f, purchase_rate: e.target.value }))}
                    className="border p-2 rounded-xl w-full text-sm outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 block mb-1">Location</label>
                <input value={onboardForm.location}
                  onChange={e => setOnboardForm(f => ({ ...f, location: e.target.value }))}
                  placeholder="Main Store"
                  className="border p-2 rounded-xl w-full text-sm outline-none" />
                <p className="text-xs text-gray-400 mt-1">Where this equipment is currently kept.</p>
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setShowOnboardModal(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleOnboard} disabled={onboardSaving}
                className="flex-1 py-2.5 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50">
                {onboardSaving ? "Adding..." : "Add Equipment ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: ISSUE TO PROJECT */}
      {issueTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Issue Equipment</h2>
                <p className="text-xs text-gray-500 mt-0.5">{issueTarget.item_name} — #{issueTarget.serial_no}</p>
              </div>
              <button onClick={() => setIssueTarget(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-3">
              <label className="text-xs font-semibold text-gray-500 block mb-1">Select Project</label>
              <select value={issueProjectId} onChange={e => setIssueProjectId(e.target.value)}
                className="border p-2 rounded-xl w-full text-sm outline-none bg-white">
                <option value="">— Select —</option>
                {projects.map(p => (
                  <option key={p.id} value={p.id}>{p.project_name}</option>
                ))}
              </select>
              {projects.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No projects with a site available. Add a site to a project first (Project → Edit).
                </p>
              )}
              <p className="text-xs text-amber-600">
                ₹{Number(issueTarget.purchase_rate || 0).toLocaleString("en-IN")} will be recorded as project cost.
              </p>
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setIssueTarget(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleConfirmIssue} disabled={issueSaving}
                className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">
                {issueSaving ? "Issuing..." : "Confirm Issue ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: EDIT LOCATION */}
      {editLocationTarget && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">Move Equipment</h2>
                <p className="text-xs text-gray-500 mt-0.5">{editLocationTarget.item_name} — #{editLocationTarget.serial_no}</p>
              </div>
              <button onClick={() => setEditLocationTarget(null)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5">
              <label className="text-xs font-semibold text-gray-500 block mb-1">New Location</label>
              <input value={editLocationValue}
                onChange={e => setEditLocationValue(e.target.value)}
                className="border p-2 rounded-xl w-full text-sm outline-none" />
            </div>
            <div className="flex gap-3 p-5 border-t">
              <button onClick={() => setEditLocationTarget(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">Cancel</button>
              <button onClick={handleSaveLocation} disabled={savingLocation}
                className="flex-1 py-2.5 rounded-xl bg-gray-700 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
                {savingLocation ? "Saving..." : "Save ✅"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: HISTORY */}
      {historyFor && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center p-5 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-800">History</h2>
                <p className="text-xs text-gray-500 mt-0.5">{historyFor.item_name} — #{historyFor.serial_no}</p>
              </div>
              <button onClick={() => { setHistoryFor(null); setHistoryLog([]); }} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
            </div>
            <div className="p-5 space-y-2">
              {historyLog.length === 0 ? (
                <p className="text-xs text-gray-400">No history yet.</p>
              ) : historyLog.map(h => (
                <div key={h.id} className="border-b border-gray-50 pb-2 text-sm">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-700 capitalize">{h.action}</span>
                    <span className={h.amount < 0 ? "text-red-500" : "text-gray-600"}>
                      {h.amount !== 0 ? `₹${h.amount.toLocaleString("en-IN")}` : ""}
                    </span>
                  </div>
                  {h.project_name && <p className="text-xs text-gray-400">{h.project_name}</p>}
                  <p className="text-xs text-gray-400">{h.performed_by_name || "—"}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}